import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch"

import behavioural from "@/assets/behavioural.webp"
import technical from "@/assets/technical.webp"
import coding from "@/assets/coding.webp"
import Tooltip from "../Tooltip";
type AssessmentConfig = {
	cutOfScore: string;
	validity_days: string;
	behavioralAssessment: {
		topic: string;
		checkSection: boolean;
		numQuestions: string;
		questionLevel: string;
		codingLanguage: string;
	};
	technicalAssessment: {

		checkSection: boolean;
		numQuestions: string;
		questionLevel: string;
		topic: string;
		codingLanguage: string;
	};
	codingAssessment: {
		checkSection: boolean;
		numQuestions: string;
		questionLevel: string;
		topic: string;
		codingLanguage: string;
	};
};

type ChildComponentProps = {
	assessmentConfig: AssessmentConfig;
	setAssessmentConfig: React.Dispatch<React.SetStateAction<AssessmentConfig>>;
};

const AssessmentForm: React.FC<ChildComponentProps> = ({
	assessmentConfig,
	setAssessmentConfig,
}) => {

	const handleSelectChange = (
		section: string,
		field: string,
		value: string | number | boolean
	) => {
		if (section !== "behavioralAssessment") {

			setAssessmentConfig((prevState: any) => ({
				...prevState,
				[section]: {
					...prevState[section],
					"checkSection": true,
					[field]: value,
				},
			}));
		} else {

			setAssessmentConfig((prevState: any) => ({
				...prevState,
				[section]: {
					...prevState[section],
					[field]: value,
				},
			}));
		}
	};

	return (

		<div className="  bg-white">

			{/* Header */}


			{/* Main Settings Card */}
			<div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
				<h2 className="text-lg font-semibold  mb-1">Assessment Settings</h2>
				<p className="text-sm text-gray-500 mb-6">Configure general assessment parameters</p>

				{/* General Settings */}
				<div className="grid grid-cols-1 md:grid-cols-2  gap-6 mb-6">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Minimum Qualification Score (%) <Tooltip position="right"><p className="w-[280px]">The minimum score required to qualify for the next stage of the assessment process. Default is 75 unless specified otherwise.</p></Tooltip>
						</label>
						<input
							min={0}
							type="number"
							value={assessmentConfig.cutOfScore}
							onChange={(e) => setAssessmentConfig({
								...assessmentConfig,
								cutOfScore: e.target.value.toString(),
							})}
							placeholder="e.g., 70%"
							className="w-full px-3 py-2 border border-gray-300 rounded-md "
						/>
						<p className="text-xs text-gray-500 mt-1">Set the minimum score required to pass</p>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Assessment Link Validity (days)
						</label>
						<input
							min={0}
							type="number"
							value={assessmentConfig.validity_days}
							onChange={(e) => setAssessmentConfig({
								...assessmentConfig,
								validity_days: e.target.value.toString(),
							})}
							placeholder="e.g., 7 days"
							className="w-full px-3 py-2 border border-gray-300 rounded-md "
						/>
						<p className="text-xs text-gray-500 mt-1">Set how long the assessment link remains active</p>
					</div>
				</div>
			</div>

			{/* Assessment Sections */}
			<div className="space-y-6">
				{/* Behavioral Assessment */}
				<div
					className="bg-white border border-gray-200 rounded-lg p-6"

				>
					<div className="flex items-start justify-between mb-4">
						<div className="flex-1">
							<div className="flex items-center gap-3 mb-2">
								<Switch

									className="data-[state=checked]:bg-[#438437] hover:cursor-not-allowed"
									checked={true}

								/>
								<h3 className="text-lg font-semibold ">Behavioral Assessment</h3>

							</div>
							<p className="text-sm text-gray-500 mb-4">Configure behavioral assessment parameters</p>

							<div className="grid grid-cols-1  gap-4 mb-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Number of Questions</label>
									<Select
										value={assessmentConfig.behavioralAssessment.numQuestions.toString()}
										onValueChange={(value) =>
											handleSelectChange(
												"behavioralAssessment",
												"numQuestions",
												parseInt(value)
											)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="No of questions" className="text-gray-300" />
										</SelectTrigger>
										<SelectContent>
											{[1, 2, 3, 4, 5].map((num) => (
												<SelectItem key={num} value={num.toString()}>
													{num}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Set the total number of behavioral questions</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level</label>
									<Select
										value={assessmentConfig.behavioralAssessment.questionLevel}
										onValueChange={(value) =>
											handleSelectChange("behavioralAssessment", "questionLevel", value)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="Difficulty level" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="Easy">Easy</SelectItem>
											<SelectItem value="Medium">Medium</SelectItem>
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Choose the complexity level of questions</p>
								</div>
							</div>
						</div>

						<div className="hidden md:block ml-6 w-80  rounded-lg ">

							<img
								src={behavioural}
								alt="Behavioral Preview"
								className="object-contain w-full max-w-[460px] h-auto"
							/>
						</div>
					</div>
				</div>

				{/* Technical Assessment */}
				<div
					className="bg-white border border-gray-200 rounded-lg p-6"
				>
					<div className="flex items-start justify-between mb-4">
						<div className="flex-1">
							<div className="flex items-center gap-3 mb-2">
								<Switch
									className="data-[state=checked]:bg-[#438437]"
									checked={assessmentConfig.technicalAssessment.checkSection}
									onCheckedChange={(checked) =>
										handleSelectChange("technicalAssessment", "checkSection", checked)
									}
								/>
								<h3 className="text-lg font-semibold ">Technical Assessment</h3>

							</div>
							<p className="text-sm text-gray-500 mb-4">Configure technical assessment parameters</p>

							<div className="grid grid-cols-1  gap-4 mb-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Number of Questions</label>
									<Select
										value={assessmentConfig.technicalAssessment.numQuestions.toString()}
										onValueChange={(value) =>
											handleSelectChange(
												"technicalAssessment",
												"numQuestions",
												parseInt(value)
											)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="No of questions" />
										</SelectTrigger>
										<SelectContent>
											{[1, 2, 3, 4, 5].map((num) => (
												<SelectItem key={num} value={num.toString()}>
													{num}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Set the total number of technical questions</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level</label>
									<Select
										value={assessmentConfig.technicalAssessment.questionLevel}
										onValueChange={(value) =>
											handleSelectChange("technicalAssessment", "questionLevel", value)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="Difficulty level" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="Easy">Easy</SelectItem>
											<SelectItem value="Medium">Medium</SelectItem>
											<SelectItem value="Hard">Hard</SelectItem>
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Choose the complexity level of questions</p>
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
								<input
									type="text"
									value={assessmentConfig.technicalAssessment.topic}
									onChange={(e) => handleSelectChange("technicalAssessment", "topic", e.target.value)}
									placeholder="Topic e.g., Cloud, Machine Learning, Internet"
									className="w-full px-3 py-2 border border-gray-300 rounded-md "
								/>
								<p className="text-xs text-gray-500 mt-1">Enter the technical topic</p>
							</div>
						</div>

						<div className="hidden md:block ml-6 w-80  rounded-lg ">

							<img
								src={technical}
								alt="Behavioral Preview"
								className="object-contain w-full max-w-[460px] h-auto"
							/>
						</div>
					</div>
				</div>

				{/* Coding Assessment */}
				<div
					className="bg-white border border-gray-200 rounded-lg p-6"
				>
					<div className="flex items-start justify-between mb-4">
						<div className="flex-1">
							<div className="flex items-center gap-3 mb-2">
								<Switch
									className="data-[state=checked]:bg-[#438437]"
									checked={assessmentConfig.codingAssessment.checkSection}
									onCheckedChange={(checked) =>
										handleSelectChange("codingAssessment", "checkSection", checked)
									}
								/>
								<h3 className="text-lg font-semibold ">Coding Assessment</h3>

							</div>
							<p className="text-sm text-gray-500 mb-4">Configure coding assessment parameters</p>

							<div className="grid grid-cols-1  gap-4 mb-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Number of Questions</label>
									<Select
										value={assessmentConfig.codingAssessment.numQuestions.toString()}
										onValueChange={(value) =>
											handleSelectChange(
												"codingAssessment",
												"numQuestions",
												parseInt(value)
											)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="No of questions" />
										</SelectTrigger>
										<SelectContent>
											{[0, 1, 2, 3].map((num) => (
												<SelectItem key={num} value={num.toString()}>
													{num}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Set the total number of coding questions</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level</label>
									<Select
										value={assessmentConfig.codingAssessment.questionLevel}
										onValueChange={(value) =>
											handleSelectChange("codingAssessment", "questionLevel", value)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="Difficulty level" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="Easy">Easy</SelectItem>
											<SelectItem value="Medium">Medium</SelectItem>
											<SelectItem value="Hard">Hard</SelectItem>
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Choose the complexity level of questions</p>
								</div>
							</div>

							<div className="grid grid-cols-1  gap-4 mb-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Programming Language</label>
									<Select
										value={assessmentConfig.codingAssessment.codingLanguage}
										onValueChange={(v) =>
											handleSelectChange("codingAssessment", "codingLanguage", v)
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="Coding Language" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="JavaScript">JavaScript</SelectItem>
											<SelectItem value="Java">Java</SelectItem>
											<SelectItem value="Python">Python</SelectItem>
											<SelectItem value="Ruby">Ruby</SelectItem>
											<SelectItem value=".Net">.Net</SelectItem>
											<SelectItem value="php">php</SelectItem>
										</SelectContent>
									</Select>
									<p className="text-xs text-gray-500 mt-1">Choose the programming language</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
									<input
										type="text"
										value={assessmentConfig.codingAssessment.topic}
										onChange={(e) => handleSelectChange("codingAssessment", "topic", e.target.value)}
										placeholder="Topic e.g., Arrays, Strings, Lists"
										className="w-full px-3 py-2 border border-gray-300 rounded-md "
									/>
									<p className="text-xs text-gray-500 mt-1">Enter the coding topic</p>
								</div>
							</div>
						</div>

						<div className="hidden md:block ml-6 w-80  rounded-lg ">

							<img
								src={coding}
								alt="Behavioral Preview"

								className="object-contain w-full max-w-[460px] h-auto"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AssessmentForm;
